'use client';

import type React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Headline } from '../headline/Headline';
import { StockItem } from '../data-market-table/types';
import { useTranslations } from '@/lib/locale-context';
import dynamic from 'next/dynamic';

const LineChart = dynamic(() => import('@/components/line-chart/LineChart'), {
  ssr: false,
});

type Props = {
  heroStockData: StockItem[];
};

const ChartWrapper: React.FC<Props> = ({ heroStockData }) => {
  const { t } = useTranslations();
  const filtered = heroStockData.filter((i) =>
    ['CH0009980894', 'XITT00BUS500', 'EU0009654078'].includes(i.isin),
  );

  const staticCharts = filtered;
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to the active chart when activeIndex changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      const scrollLeft = activeIndex * scrollContainerRef.current.offsetWidth;
      scrollContainerRef.current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }
  }, [activeIndex]);

  // Update activeIndex when user scrolls
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let scrollTimeout: NodeJS.Timeout | null = null;

    const handleScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(() => {
        const newIndex = Math.round(
          container.scrollLeft / container.offsetWidth,
        );
        setActiveIndex(newIndex);
      }, 100); // Debounce scroll event
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, []);

  return (
    <div className="container mx-auto h-full pt-[40px] sm:px-0">
      <Headline
        variant="headingOne"
        title={t('markets.marketOverview')}
        className="pb-[20px] text-brand-700"
      />
      <div
        ref={scrollContainerRef}
        className="no-scrollbar flex snap-x snap-mandatory overflow-x-auto pb-4 md:grid md:grid-cols-2 md:gap-6 lg:grid-cols-3"
      >
        {staticCharts.map((chart: StockItem, index) => (
          <div
            key={index}
            className="w-full flex-shrink-0 snap-center md:w-auto md:flex-shrink"
          >
            <LineChart
              title={chart.shortName.toUpperCase()}
              productId={chart.id}
              interval="YTD"
              stockData={chart}
            />
          </div>
        ))}
      </div>
      {/* Pagination dots for mobile */}
      <div className="mt-4 flex justify-center gap-2 md:hidden">
        {staticCharts.map((_, index) => (
          <button
            key={index}
            className={`h-2 w-2 rounded-full ${activeIndex === index ? 'bg-gray-800' : 'bg-gray-300'}`}
            onClick={() => setActiveIndex(index)}
            aria-label={`Go to chart ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default ChartWrapper;
